import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import {
  DateAdapter,
  MAT_DATE_FORMATS,
  MAT_DATE_LOCALE,
} from '@angular/material/core';
import { MomentDateAdapter } from '@angular/material-moment-adapter';
import { LabelService } from '../../services/label.service';
import { newPatientAndAppointment } from '../../models/calendar-model';
import moment from 'moment';
import { combineLatest } from 'rxjs';

export const MY_DATE_FORMATS = {
  parse: {
    dateInput: 'DD/MM/YYYY',
  },
  display: {
    dateInput: 'DD/MM/YYYY',
    monthYearLabel: 'MMM YYYY',
    dateA11yLabel: 'LL',
    monthYearA11yLabel: 'MMMM YYYY',
  },
};

@Component({
  selector: 'app-add-new-patient-panel',
  templateUrl: './add-new-patient-panel.component.html',
  styleUrls: ['./add-new-patient-panel.component.scss'],
  providers: [
    {
      provide: DateAdapter,
      useClass: MomentDateAdapter,
      deps: [MAT_DATE_LOCALE],
    },
    { provide: MAT_DATE_FORMATS, useValue: MY_DATE_FORMATS },
  ],
  standalone: false,
})
export class AddNewPatientPanelComponent implements OnInit, OnChanges {
  @Input() numberOfAppointments: number = 0;
  @Input() title: string = '';
  @Input() onlyNewAppointment?: boolean = false;
  @Input() newAppointmentTitle: string = 'Appuntamento N°1';
  @Input() newAppointmentDay: string = '';
  @Input() newAppointmentMonth: string = '';
  @Input() newAppointmentYear: string = '';
  @Input() newAppointmentHours: string = '';
  @Input() newAppointmentMinutes: string = '';
  @Input() newAppointmentLenght: string = '45';
  @Input() patientNameAndSurname?: string = '';

  @Output() cancel = new EventEmitter<void>();
  @Output() confirm = new EventEmitter<newPatientAndAppointment>();

  patientForm: FormGroup;
  isBtnClicked: boolean = false;

  constructor(
    public labelService: LabelService,
    private fb: FormBuilder,
    private dateAdapter: DateAdapter<any>
  ) {
    this.patientForm = this.fb.group({
      firstName: [''],
      lastName: [''],
      appointmentTitle: [''],
      appointmentDate: [''],
      appointmentTime: [''],
      appointmentLength: [''],
      isRemote: [false],
      isNewAppointmentSelected: [false],
    });

    this.dateAdapter.setLocale('it-IT');
  }
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['newAppointmentTitle']) {
      this.populateFormFields();
    }
    if (changes['numberOfAppointments']) {
      this.newAppointmentTitle = `${
        this.patientNameAndSurname ? this.patientNameAndSurname + ': ' : ''
      }Appuntamento N° ${this.numberOfAppointments + 1}`;
      this.populateFormFields();
    }
  }

  ngOnInit(): void {
    this.populateFormFields();
    this.updateFormValidation();

    if (!this.onlyNewAppointment) {
      this.patientForm.get('firstName')?.valueChanges.subscribe(() => {
        this.updateTitle();
      });

      this.patientForm.get('lastName')?.valueChanges.subscribe(() => {
        this.updateTitle();
      });
    }
  }

  updateTitle(): void {
    this.patientForm
      .get('appointmentTitle')
      ?.setValue(
        `${this.patientForm.get('firstName')?.value || ''} ${
          this.patientForm.get('lastName')?.value || ''
        }: Appuntamento N°1`
      );
  }

  populateFormFields(): void {
    const dateString = `${this.newAppointmentDay}/${this.newAppointmentMonth}/${this.newAppointmentYear}`;
    let timeString = `${this.newAppointmentHours}:${this.newAppointmentMinutes}`;
    if (timeString === ':') {
      timeString = '00:00';
    }

    this.patientForm.patchValue({
      appointmentTitle: this.newAppointmentTitle,
      appointmentDate: moment(dateString, 'DD/MM/YYYY'),
      appointmentTime: timeString,
      appointmentLength: this.newAppointmentLenght,
    });

    if (this.title === 'new_patient') {
      this.patientForm.get('firstName')?.setValidators(Validators.required);
      this.patientForm.get('lastName')?.setValidators(Validators.required);
    } else {
      this.patientForm.get('firstName')?.clearValidators();
      this.patientForm.get('lastName')?.clearValidators();
    }
    this.patientForm.get('firstName')?.updateValueAndValidity();
    this.patientForm.get('lastName')?.updateValueAndValidity();
  }

  updateFormValidation(): void {
    const appointmentControls = [
      'appointmentTitle',
      'appointmentDate',
      'appointmentTime',
      'appointmentLength',
    ];

    this.patientForm
      .get('isNewAppointmentSelected')
      ?.valueChanges.subscribe((isSelected) => {
        appointmentControls.forEach((control) => {
          if (isSelected || this.onlyNewAppointment) {
            this.patientForm.get(control)?.setValidators(Validators.required);
          } else {
            this.patientForm.get(control)?.clearValidators();
          }
          this.patientForm.get(control)?.updateValueAndValidity();
        });
      });

    // Initial validation setup
    if (this.onlyNewAppointment) {
      appointmentControls.forEach((control) => {
        this.patientForm.get(control)?.setValidators(Validators.required);
        this.patientForm.get(control)?.updateValueAndValidity();
      });
    }
  }

  onCancel(): void {
    this.cancel.emit();
  }

  onConfirm(): void {
    if (this.isBtnClicked || this.patientForm.invalid) {
      return;
    }

    const formValues = this.patientForm.value;
    const dateString = formValues.appointmentDate
      ? moment(formValues.appointmentDate).format('DD/MM/YYYY')
      : '';

    const output: newPatientAndAppointment = {
      first_name: formValues.firstName,
      last_name: formValues.lastName,
      title:
        this.onlyNewAppointment ||
        this.patientForm.get('isNewAppointmentSelected')?.value
          ? formValues.appointmentTitle
          : '',
      date: dateString,
      time: formValues.appointmentTime || '',
      lenght: formValues.appointmentLength || '',
      isRemote: formValues.isRemote,
    };

    this.isBtnClicked = true;
    this.confirm.emit(output);
  }
}
